/**
 * Institutions Service Utilities
 *
 * Helper functions for institution operations, formatting, and validation
 */

import type { Institution } from './types';

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format institution name with abbreviation
 */
export function formatInstitutionName(institution: Institution): string {
  if (institution.abbreviation) {
    return `${institution.name} (${institution.abbreviation})`;
  }
  return institution.name;
}

/**
 * Get institution type label
 */
export function getInstitutionTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    bank: 'Bank',
    superannuation: 'Superannuation Fund',
    insurance: 'Insurance Company',
    investment: 'Investment Firm',
    government: 'Government Agency',
    utility: 'Utility Provider',
    telecommunications: 'Telecommunications',
    healthcare: 'Healthcare Provider',
    legal: 'Legal Services',
    other: 'Other'
  };

  return typeLabels[type] || type;
}

// ============================================================================
// Cache Key Functions
// ============================================================================

/**
 * Get cache key for institutions list
 */
export function getInstitutionsCacheKey(filters?: unknown): string[] {
  const baseKey = ['institutions'];

  if (filters) {
    return [...baseKey, JSON.stringify(filters)];
  }

  return baseKey;
}

/**
 * Get cache key for single institution
 */
export function getInstitutionCacheKey(id: number): string[] {
  return ['institution', id.toString()];
}

/**
 * Get cache key for institution types
 */
export function getInstitutionTypesCacheKey(): string[] {
  return ['institution-types'];
}

/**
 * Get cache key for single institution type
 */
export function getInstitutionTypeCacheKey(slug: string): string[] {
  return ['institution-type', slug];
}

/**
 * Get cache key for matter institutions
 */
export function getMatterInstitutionsCacheKey(matterId: number): string[] {
  return ['matter-institutions', matterId.toString()];
}

/**
 * Get cache key for the grouped (notifiable, popular-first) institutions
 * listing that feeds the Discovery add-institution drawer.
 */
export function getGroupedInstitutionsCacheKey(): string[] {
  return ['institutions', 'grouped'];
}

/**
 * Get cache key for the letter-type registry — what a solicitor may ask a
 * party, served from the backend so the labels have one definition.
 */
export function getLetterTypesCacheKey(): string[] {
  return ['institutions', 'letter-types'];
}
